const API_BASE = '/api';

const getToken = () => localStorage.getItem('token');

const apiRequest = async (url: string, options: RequestInit = {}) => {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
      throw new Error('未授权，请重新登录');
    }
    const error = await response.json().catch(() => ({ error: '请求失败' }));
    throw new Error(error.error || error.message || '请求失败');
  }

  return response.json();
};

export type FeedbackCategory = '建议' | '吐槽/反馈' | '问题举报' | '求助' | '投票讨论' | '表扬';

export interface FeedbackMediaItem {
  key: string;
  url: string;
  type: 'image' | 'video';
  fileName: string;
  fileSize: number;
  duration: number;
  thumbnail: string;
  status: 'uploading' | 'ready' | 'failed';
}

export interface FeedbackPost {
  _id: string;
  author: string;
  anonymousName: string;
  title: string;
  content: string;
  category: FeedbackCategory;
  tags: string[];
  media: FeedbackMediaItem[];
  status: 'pending' | 'accepted' | 'processing' | 'resolved' | 'rejected';
  allowComment: boolean;
  adminOnlyReply: boolean;
  likeCount: number;
  disagreeCount: number;
  commentCount: number;
  reportCount: number;
  isHidden: boolean;
  isDeleted?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackComment {
  _id: string;
  post: string;
  author: string;
  parent: string | null;
  anonymousName: string;
  content: string;
  isOfficial: boolean;
  likeCount: number;
  reportCount: number;
  status: 'visible' | 'under_review' | 'hidden' | 'deleted';
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackTicket {
  _id: string;
  post: string;
  assignee: string | null;
  status: 'pending' | 'accepted' | 'in_progress' | 'resolved' | 'rejected';
  progressDetail: string;
  resolutionSummary: string;
  expectedFinishAt?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackPostListResult {
  total: number;
  page: number;
  pageSize: number;
  items: FeedbackPost[];
}

export interface FeedbackCommentListResult {
  total: number;
  page: number;
  pageSize: number;
  items: FeedbackComment[];
}

export interface FeedbackAnalyticsResult {
  totalThisWeek: number;
  byCategory: Array<{ _id: string; count: number }>;
  byStatus: Array<{ _id: string; count: number }>;
  averageResponseHours: number | null;
  mostLikedPosts: FeedbackPost[];
  mostDisagreedPosts: FeedbackPost[];
}

export const feedbackService = {
  listPosts: async (params: {
    page?: number;
    pageSize?: number;
    category?: string;
    status?: string;
    sort?: 'hot' | 'new' | 'pending';
  } = {}): Promise<FeedbackPostListResult> => {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.pageSize) query.append('pageSize', String(params.pageSize));
    if (params.category) query.append('category', params.category);
    if (params.status) query.append('status', params.status);
    if (params.sort) query.append('sort', params.sort);
    const res = await apiRequest(`/feedback/posts?${query.toString()}`);
    return res.data;
  },

  getPost: async (id: string): Promise<{ post: FeedbackPost; ticket: FeedbackTicket | null }> => {
    const res = await apiRequest(`/feedback/posts/${id}`);
    return res.data;
  },

  createPost: async (data: {
    title: string;
    content: string;
    category: FeedbackCategory;
    tags?: string[];
    media?: FeedbackMediaItem[];
    allowComment?: boolean;
    adminOnlyReply?: boolean;
  }): Promise<FeedbackPost> => {
    const res = await apiRequest('/feedback/posts', {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        tags: data.tags || [],
        media: data.media || [],
      }),
    });
    return res.data;
  },

  deletePost: async (id: string): Promise<void> => {
    await apiRequest(`/feedback/posts/${id}`, { method: 'DELETE' });
  },

  votePost: async (id: string, type: 'like' | 'disagree'): Promise<FeedbackPost> => {
    const res = await apiRequest(`/feedback/posts/${id}/vote`, {
      method: 'POST',
      body: JSON.stringify({ type }),
    });
    return res.data;
  },

  reportPost: async (id: string, reasonType: string, reasonDetail?: string): Promise<void> => {
    await apiRequest(`/feedback/posts/${id}/report`, {
      method: 'POST',
      body: JSON.stringify({ reasonType, reasonDetail }),
    });
  },

  listComments: async (postId: string, params: { page?: number; pageSize?: number } = {}): Promise<FeedbackCommentListResult> => {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.pageSize) query.append('pageSize', String(params.pageSize));
    const res = await apiRequest(`/feedback/posts/${postId}/comments?${query.toString()}`);
    return res.data;
  },

  createComment: async (postId: string, content: string, parentId?: string): Promise<FeedbackComment> => {
    const res = await apiRequest(`/feedback/posts/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content, parentId }),
    });
    return res.data;
  },

  voteComment: async (commentId: string, type: 'like' | 'disagree'): Promise<FeedbackComment> => {
    const res = await apiRequest(`/feedback/comments/${commentId}/vote`, {
      method: 'POST',
      body: JSON.stringify({ type }),
    });
    return res.data;
  },

  reportComment: async (commentId: string, reasonType: string, reasonDetail?: string): Promise<void> => {
    await apiRequest(`/feedback/comments/${commentId}/report`, {
      method: 'POST',
      body: JSON.stringify({ reasonType, reasonDetail }),
    });
  },

  createOfficialReply: async (postId: string, content: string, parentId?: string): Promise<FeedbackComment> => {
    const res = await apiRequest(`/feedback/posts/${postId}/official-reply`, {
      method: 'POST',
      body: JSON.stringify({ content, parentId }),
    });
    return res.data;
  },

  listMyPosts: async (params: { page?: number; pageSize?: number } = {}): Promise<FeedbackPostListResult> => {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.pageSize) query.append('pageSize', String(params.pageSize));
    const res = await apiRequest(`/feedback/me/posts?${query.toString()}`);
    return res.data;
  },

  listMyComments: async (params: { page?: number; pageSize?: number } = {}): Promise<FeedbackCommentListResult> => {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.pageSize) query.append('pageSize', String(params.pageSize));
    const res = await apiRequest(`/feedback/me/comments?${query.toString()}`);
    return res.data;
  },

  listMyReports: async (params: { page?: number; pageSize?: number } = {}): Promise<any> => {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.pageSize) query.append('pageSize', String(params.pageSize));
    const res = await apiRequest(`/feedback/me/reports?${query.toString()}`);
    return res.data;
  },

  uploadMedia: async (files: File[]): Promise<FeedbackMediaItem[]> => {
    const token = getToken();
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await fetch(`${API_BASE}/feedback/media`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '上传失败' }));
      throw new Error(error.error || error.message || '上传失败');
    }

    const result = await response.json();
    return result.data.media as FeedbackMediaItem[];
  },

  getReviewQueue: async (): Promise<{
    posts: FeedbackPost[];
    comments: FeedbackComment[];
    reports: any[];
  }> => {
    const res = await apiRequest('/feedback/admin/review-queue');
    return res.data;
  },

  updatePostStatus: async (postId: string, payload: {
    status: FeedbackTicket['status'];
    reason?: string;
    progressDetail?: string;
    resolutionSummary?: string;
    assigneeId?: string;
    expectedFinishAt?: string;
  }): Promise<{ post: FeedbackPost; ticket: FeedbackTicket }> => {
    const res = await apiRequest(`/feedback/admin/posts/${postId}/status`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return res.data;
  },

  assignTicket: async (ticketId: string, assigneeId: string): Promise<FeedbackTicket> => {
    const res = await apiRequest(`/feedback/admin/tickets/${ticketId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ assigneeId }),
    });
    return res.data;
  },

  updateTicketProgress: async (ticketId: string, payload: {
    progressDetail?: string;
    resolutionSummary?: string;
    status?: FeedbackTicket['status'];
  }): Promise<FeedbackTicket> => {
    const res = await apiRequest(`/feedback/admin/tickets/${ticketId}/progress`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return res.data;
  },

  getAnalytics: async (): Promise<FeedbackAnalyticsResult> => {
    const res = await apiRequest('/feedback/admin/analytics');
    return res.data;
  },

  listSensitiveWords: async (): Promise<any[]> => {
    const res = await apiRequest('/feedback/admin/sensitive-words');
    return res.data;
  },

  createSensitiveWord: async (payload: { word: string; level: string; replaceWith?: string; description?: string }): Promise<any> => {
    const res = await apiRequest('/feedback/admin/sensitive-words', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return res.data;
  },

  updateSensitiveWord: async (id: string, payload: { level?: string; replaceWith?: string; description?: string; enabled?: boolean }): Promise<any> => {
    const res = await apiRequest(`/feedback/admin/sensitive-words/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return res.data;
  },

  deleteSensitiveWord: async (id: string): Promise<void> => {
    await apiRequest(`/feedback/admin/sensitive-words/${id}`, {
      method: 'DELETE',
    });
  },
};

